import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { TranslateService } from '@ngx-translate/core'
import {
  AlertService,
  AudienceSummary,
  Campaign,
  ChannelTypeEnum,
  ContactGroup,
  Tag,
  WaTemplateResponse,
} from 'lib-trend-core'

export interface CampaingSaveConfirmData {
  leadCount: number
  cost: number
}

type CampaingSaveConfirmModalData = {
  campaign: Campaign,
  ignoredTags: Tag[],
  ignoredContactGroups: ContactGroup[],
  selectedTemplate?: Partial<WaTemplateResponse>,
  channel: { type: ChannelTypeEnum },
  audienceSummary: AudienceSummary | null,
  audienceText: string,
  /** Price of one message, as the wizard quoted it. Null = no figure. */
  unitPrice?: number | null,
  estimatedCost?: number,
  /** ISO-4217 code the two figures above are in. */
  priceCurrency?: string,
  /** The wizard knows what is being priced and has no figure for it. */
  unpriced?: boolean,
  /**
   * i18n key of what to say in place of the figure when `unpriced` is set. The
   * reason depends on who bills the channel — a wallet category nobody priced
   * is not a template Meta has no cost history for — and only the wizard knows
   * which one applies.
   */
  unpricedLabel?: string
}

@Component({
    selector: 'app-campaing-save-confirm-modal',
    templateUrl: './campaing-save-confirm-modal.v2.component.html',
    // templateUrl: './campaing-save-confirm-modal.component.html',
    styleUrl: './campaing-save-confirm-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CampaingSaveConfirmModalComponent implements OnInit {
  confirmedData: CampaingSaveConfirmData
  campaign: Campaign
  ignoredTags: Tag[]
  ignoredContactGroups: ContactGroup[]
  audience: string
  audienceSummary: AudienceSummary | null

  /**
   * Handed in by the wizard, never computed here. This modal used to carry its
   * own copy of Meta's rate card — a per-conversation table, in dollars, from a
   * pricing model Meta retired — and render it with a `BRL` pipe, so the last
   * screen before confirming quoted different money from the panel behind it.
   */
  unitPrice: number | null = null
  cost = 0.0
  priceCurrency = 'BRL'
  unpriced = false
  /**
   * Shown instead of the cost when `unpriced`. The default is the text this
   * modal carried before the wizard started handing the reason in.
   */
  unpricedLabel = 'campaign.campaignForm.priceNoHistory'

  selectedTemplate: Partial<WaTemplateResponse>

  constructor(
    public dialogRef: MatDialogRef<CampaingSaveConfirmModalComponent>,
    public alertService: AlertService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: CampaingSaveConfirmModalData
  ) {
    this.campaign = data.campaign;
    this.ignoredTags = data.ignoredTags;
    this.ignoredContactGroups = data.ignoredContactGroups;
    this.selectedTemplate = data.selectedTemplate;
    this.audienceSummary = data.audienceSummary ?? null;
    this.audience = data.audienceText;
    this.unitPrice = data.unitPrice ?? null;
    this.cost = data.estimatedCost ?? 0;
    this.priceCurrency = data.priceCurrency ?? 'BRL';
    this.unpriced = data.unpriced ?? false;
    this.unpricedLabel = data.unpricedLabel || this.unpricedLabel;
  }

  get totalToSend(): number | null {
    return this.audienceSummary?.totalToSend ?? null;
  }

  get cannotConfirm(): boolean {
    return this.totalToSend === null || this.totalToSend === 0;
  }

  ngOnInit(): void {
    if (!this.campaign) return;

    if (!this.audience) {
      this.audience = this.translate.instant('campaign.campaignForm.audienceUndefined');
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }

  confirmSaveCampaign() {
    const total = this.totalToSend;
    if (total === null || total === 0) {
      this.alertService.error(this.translate.instant('campaign.saveConfirm.noLeadsFound'));
      return;
    }

    this.confirmedData = {
      leadCount: total,
      cost: this.cost,
    }
    this.dialogRef.close(this.confirmedData);
  }
}
